<script setup lang="ts">
import { useGetAdminCompanyStatistics, useInvalidateCompanyStatisticsCache } from '@swipe4work/api-orval'
import type { GetAdminCompanyStatisticsJobBreakdownDto, GetAdminCompanyStatisticsReasonStatDto } from '@swipe4work/models-orval'
import { MetabaseResourceType } from '@swipe4work/models-orval'
import { adminGetMetabaseIframeUrl } from '@swipe4work/api-orval'
import DateRangePicker from '@/shared/components/form/DateRangePicker.vue'
import CacheStatusIndicator from '@/shared/components/analytics/CacheStatusIndicator.vue'
import StatTile from '@/shared/components/analytics/StatTile.vue'
import CompanyJobsExportDialog from '@/features/companies/dialogs/CompanyJobsExportDialog.vue'
import { buildStatisticsRangePresets, endOfMonth, startOfMonth } from '@/features/sales/composables/useSalesMetricsAdapter'
import { useJobAdapter } from '@/shared/adapters/matching/useJobAdapter'
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import { useQueryClient } from '@tanstack/vue-query'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core'
import type { ViewCompanyType } from '@/shared/stores/CompanyStore'

definePage({
  path: '/companies/:id/statistics',
  name: 'companies.[id].statistics',
  meta: {
    permission: {
      resource: 'CompanyStatistics',
      level: 'Read',
    },
  },
})

const companyId = inject<Ref<number>>('companyId')!
const company = inject<Ref<ViewCompanyType | undefined>>('company')!

const rangePresets = buildStatisticsRangePresets()
const today = new Date()
const dateRange = ref<{
  from: Date | null,
  to: Date | null
} | null>({
  from: startOfMonth(today),
  to: endOfMonth(today),
})
const includeSubCompanies = ref(false)

const hasSubCompanies = computed(() =>
  (company.value?.subCompanies?.length ?? 0) > 0,
)

const queryParams = computed(() => ({
  fromDate: dateRange.value?.from ?? undefined,
  toDate: dateRange.value?.to ?? undefined,
  includeSubCompanies: includeSubCompanies.value || undefined,
}))

const { data, isPending, isFetching, refetch } = useGetAdminCompanyStatistics(
  companyId,
  queryParams,
  {
    query: {
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => companyId.value > 0),
    },
  },
)

const stats = computed(() => data.value ?? null)

const queryClient = useQueryClient()
const { mutateAsync: invalidateCache, isPending: isInvalidating } = useInvalidateCompanyStatisticsCache()

async function refresh() {
  await invalidateCache({
    companyId: companyId.value,
    params: queryParams.value,
  })
  await queryClient.invalidateQueries({
    queryKey: [
      'admin',
      'company',
      companyId.value,
      'statistics',
    ],
  })
  await refetch()
}

const cachedAt = computed(() => stats.value?.cachedAt ? new Date(stats.value.cachedAt) : null)
const cacheExpiresAt = computed(() => stats.value?.cacheExpiresAt ? new Date(stats.value.cacheExpiresAt) : null)

const funnelSteps = computed(() => {
  if (!stats.value) return []
  const s = stats.value

  const swiped = s.swipeCount
  const applied = s.applicationCount
  const reviewed = s.matchCount + s.rejectionCount + s.closedMatchCount + s.hireCount
  const matched = s.matchCount + s.closedMatchCount + s.hireCount
  const closed = s.closedMatchCount + s.hireCount

  return [
    {
      label: 'MatchScores berekend',
      count: s.matchScoreCount,
      color: '#6366F1',
      description: 'Totaal aantal kandidaten waarvoor de matching algoritmes een score hebben berekend voor de vacatures van dit bedrijf.',
    },
    {
      label: 'Vacatures zichtbaar',
      count: s.visibleMatchScoreCount,
      color: '#8B5CF6',
      description: 'Kandidaten aan wie de vacatures van dit bedrijf daadwerkelijk getoond worden (score boven drempel, niet uitgesloten).',
      dropOffLabel: 'Niet zichtbaar',
      dropOffDescription: 'Score onder de drempel of uitgesloten door het bedrijf.',
    },
    {
      label: 'Geswiped',
      count: swiped,
      color: '#0EA5E9',
      description: 'Kandidaten die een vacature van dit bedrijf links of rechts hebben geswiped.',
      dropOffLabel: 'Openstaand op de stapel',
      dropOffDescription: 'Vacature wel geopend, maar nog geen swipe gedaan.',
    },
    {
      label: 'Gesolliciteerd',
      count: applied,
      color: '#3B82F6',
      description: 'Kandidaten die rechts hebben geswiped en daarmee interesse hebben getoond.',
      dropOffLabel: 'Naar links geswiped',
      dropOffDescription: 'Kandidaat had geen interesse en heeft de vacature weggeswiped.',
    },
    {
      label: 'Beoordeeld door werkgever',
      count: reviewed,
      color: '#F59E0B',
      description: 'Sollicitaties waarop het bedrijf inmiddels heeft gereageerd (gematched of afgewezen).',
      dropOffLabel: 'Nog te beoordelen',
      dropOffDescription: 'Sollicitaties die nog wachten op een reactie van het bedrijf.',
    },
    {
      label: 'Gematched',
      count: matched,
      color: '#22C55E',
      description: 'Sollicitaties geaccepteerd door de werkgever (chat geopend).',
      dropOffLabel: 'Afgewezen',
      dropOffDescription: 'Sollicitatie is afgewezen door de werkgever.',
    },
    {
      label: 'Gesloten',
      count: closed,
      color: '#14B8A6',
      description: 'Matches die zijn afgesloten (aangenomen, niet bereikbaar of niet geschikt).',
      dropOffLabel: 'Open match',
      dropOffDescription: 'Match staat nog open en wordt actief opgevolgd door het bedrijf.',
    },
    {
      label: 'Aangenomen',
      count: s.hireCount,
      color: '#10B981',
      description: 'Matches die hebben geleid tot een daadwerkelijke aanname.',
      dropOffLabel: 'Niet aangenomen',
      dropOffDescription: 'Match is afgesloten zonder hire.',
    },
  ]
})

const impressionRatio = computed(() => {
  if (!stats.value || stats.value.visibleMatchScoreCount === 0) return null

  return stats.value.uniqueImpressionCount / stats.value.visibleMatchScoreCount * 100
})

const swipeRatio = computed(() => {
  if (!stats.value || stats.value.visibleMatchScoreCount === 0) return null

  return stats.value.swipeCount / stats.value.visibleMatchScoreCount * 100
})

function formatRatio(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'

  return `${value.toFixed(1)}%`
}

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'

  return value.toLocaleString('nl-NL')
}

const rejectionPalette = [
  '#EF4444',
  '#F97316',
  '#EAB308',
  '#22C55E',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
]

const closePalette = [
  '#10B981',
  '#64748B',
  '#F97316',
  '#EF4444',
]

function buildPieItems(reasons: GetAdminCompanyStatisticsReasonStatDto[], palette: string[]) {
  return reasons.map((r, i) => ({
    label: r.reason,
    count: r.count,
    color: palette[i % palette.length],
  }))
}

const rejectionItems = computed(() => buildPieItems(stats.value?.rejectionsByReason ?? [], rejectionPalette))
const closeItems = computed(() => buildPieItems(stats.value?.matchCloseReasonsByReason ?? [], closePalette))

const jobBreakdown = computed<GetAdminCompanyStatisticsJobBreakdownDto[]>(() => stats.value?.jobBreakdown ?? [])

const jobStatusOptions = computed(() => {
  const statuses = new Set(jobBreakdown.value.map(j => j.status))

  return [ ...statuses ].sort().map(status => ({
    label: status,
    value: status,
  }))
})

const tableFilters = ref({
  global: {
    value: null as string | null,
    matchMode: FilterMatchMode.CONTAINS,
  },
  status: {
    value: null as string | null,
    matchMode: FilterMatchMode.EQUALS,
  },
})

const draftStatus = ref<string | null>(null)

const activeFilterCount = computed(() => (tableFilters.value.status.value ? 1 : 0))

function onFilterPanelShow() {
  draftStatus.value = tableFilters.value.status.value
}

function onFilterApply() {
  tableFilters.value.status.value = draftStatus.value
}

function onFilterClear() {
  draftStatus.value = null
  tableFilters.value.status.value = null
}

const { jobStatusLabel, jobStatusSeverity } = useJobAdapter()

const dialog = useDynamicDialog()

function openExportDialog() {
  dialog.open(CompanyJobsExportDialog, {
    data: {
      companyId: companyId.value,
      initialDateRange: dateRange.value,
      includeSubCompanies: includeSubCompanies.value,
      initialStatus: tableFilters.value.status.value,
      jobStatusOptions: jobStatusOptions.value,
    },
  })
}

const columnHeaderPt = { columnTitle: '!text-xs' }

const metabaseIframeUrl = ref<string | null>(null)

watch(() => company.value?.name, async (name) => {
  if (!name) {
    metabaseIframeUrl.value = null

    return
  }

  const res = await adminGetMetabaseIframeUrl({
    resourceType: MetabaseResourceType.Dashboard,
    resourceId: '12',
    parameters: { bedrijf: name },
  })

  const url = new URL(res.url)

  url.hash = '#background=false&bordered=false&locale=nl&theme=transparent'
  url.searchParams.set('datum', 'past30days')
  metabaseIframeUrl.value = url.toString()
}, { immediate: true })
</script>

<template>
  <div class="max-w-8xl mx-auto space-y-5">
    <div class="flex flex-wrap items-end gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-xs text-surface-500 font-medium tracking-wide uppercase">Periode</label>
        <DateRangePicker v-model="dateRange" :presets="rangePresets" placeholder="Alle data" />
      </div>
      <div v-if="hasSubCompanies" class="flex flex-col gap-1">
        <label class="text-xs text-surface-500 font-medium tracking-wide uppercase">Scope</label>
        <SelectButton
          v-model="includeSubCompanies"
          :options="[
            { label: 'Dit bedrijf', value: false },
            { label: 'Incl. subbedrijven', value: true },
          ]"
          option-label="label"
          option-value="value"
          size="small"
        />
      </div>
      <CacheStatusIndicator
        class="ml-auto"
        :cached-at="cachedAt"
        :expires-at="cacheExpiresAt"
        :is-refreshing="isInvalidating || isFetching"
        @refresh="refresh"
      />
    </div>

    <div v-if="isPending" class="flex flex-col gap-4">
      <Skeleton height="8rem" />
      <Skeleton height="14rem" />
      <Skeleton height="20rem" />
    </div>

    <template v-else-if="stats">
      <FunnelCard title="Bedrijf Journey" :steps="funnelSteps" />

      <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="Totale matchscores"
          :value="formatNumber(stats.matchScoreCount)"
          subtitle="Totale mogelijke weergaven (reach)"
        />
        <StatTile
          label="Unieke impressies"
          :value="formatNumber(stats.uniqueImpressionCount)"
          subtitle="Vacatures voor het eerst geopend"
        />
        <StatTile
          label="Totaal geswiped"
          :value="formatNumber(stats.swipeCount)"
          subtitle="Links + rechts swipes"
        />
        <StatTile
          label="Totaal gesolliciteerd"
          :value="formatNumber(stats.applicationCount)"
          subtitle="Rechts geswiped"
        />
        <StatTile
          label="Totale matches"
          :value="formatNumber(stats.matchCount + stats.closedMatchCount + stats.hireCount)"
          subtitle="Incl. gesloten en hires"
        />
        <StatTile
          label="Impressie ratio"
          :value="formatRatio(impressionRatio)"
          subtitle="Impressies t.o.v. zichtbare matchscores"
        />
        <StatTile
          label="Sollicitatie / match ratio"
          :value="formatRatio(stats.matchRatio)"
          subtitle="Matches t.o.v. sollicitaties"
        />
        <StatTile
          label="Swipe ratio"
          :value="formatRatio(swipeRatio)"
          subtitle="Swipes t.o.v. zichtbare matchscores"
        />
      </div>

      <CardWithHeader
        title="Vacature overzicht"
        :options="{ removeBodyPadding: true }"
      >
        <template #header-actions>
          <div class="flex items-center gap-3">
            <Button
              outlined
              severity="secondary"
              size="small"
              class="bg-surface-0"
              @click="openExportDialog"
            >
              <i class="fa-regular fa-file-export mr-2" />
              Exporteren
            </Button>

            <DataTableFilterPanel
              :active-filter-count="activeFilterCount"
              @show="onFilterPanelShow"
              @apply="onFilterApply"
              @clear="onFilterClear"
            >
              <div class="flex flex-col gap-1">
                <label class="text-xs text-surface-500 font-medium tracking-wide uppercase">Status</label>
                <Select
                  v-model="draftStatus"
                  :options="jobStatusOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Alle statussen"
                  show-clear
                  size="small"
                  class="w-full"
                />
              </div>
            </DataTableFilterPanel>

            <IconField class="max-w-xs flex-1">
              <InputIcon class="fa-regular fa-search" />
              <InputText
                v-model="tableFilters.global.value"
                placeholder="Zoeken op titel..."
                class="w-full"
              />
              <InputIcon
                v-if="tableFilters.global.value"
                class="fa-regular fa-xmark cursor-pointer"
                style="pointer-events: auto"
                @click="tableFilters.global.value = null"
              />
            </IconField>
          </div>
        </template>

        <DataTable
          v-model:filters="tableFilters"
          :value="jobBreakdown"
          data-key="jobId"
          striped-rows
          :paginator="jobBreakdown.length > 10"
          :rows="10"
          :row-hover="true"
          sort-field="publishedAt"
          :sort-order="-1"
          :global-filter-fields="['title']"
          size="small"
          table-style="table-layout: fixed"
        >
          <template #empty>
            <div class="py-6 text-center text-sm text-surface-500">
              Geen vacatures met statistieken in deze periode.
            </div>
          </template>
          <Column field="title" header="Vacature" sortable style="min-width: 14rem" :pt="columnHeaderPt">
            <template #body="{ data: row }">
              <RouterLink
                :to="{ name: 'vacatures.detail', params: { id: row.jobId } }"
                class="line-clamp-1 text-sm text-primary-500 hover:underline"
              >
                {{ row.title }}
              </RouterLink>
            </template>
          </Column>
          <Column field="status" header="Status" sortable style="width: 6rem" :pt="columnHeaderPt">
            <template #body="{ data: row }">
              <PillTag
                :value="jobStatusLabel(row.status)"
                :severity="jobStatusSeverity(row.status)"
              />
            </template>
          </Column>
          <DateColumn
            key="publishedAt"
            header="Gepubliceerd"
            field="publishedAt"
            classes="text-slate-600 text-xs"
            sortable
            format="DD-MM-YY"
            style="width: 6rem"
            :pt="columnHeaderPt"
          />
          <DateColumn
            key="createdAt"
            header="Aangemaakt"
            field="createdAt"
            classes="text-slate-600 text-xs"
            sortable
            format="DD-MM-YY"
            style="width: 6rem"
            :pt="columnHeaderPt"
          />
          <Column field="matchScoreCount" header="MatchScores" sortable style="width: 5rem" :pt="columnHeaderPt" body-class="tabular-nums text-xs">
            <template #body="{ data: row }">
              {{ formatNumber(row.matchScoreCount) }}
            </template>
          </Column>
          <Column field="visibleMatchScoreCount" header="Zichtbaar" sortable style="width: 5rem" :pt="columnHeaderPt" body-class="tabular-nums text-xs">
            <template #body="{ data: row }">
              {{ formatNumber(row.visibleMatchScoreCount) }}
            </template>
          </Column>
          <Column field="uniqueImpressionCount" header="Impressies" sortable style="width: 5rem" :pt="columnHeaderPt" body-class="tabular-nums text-xs">
            <template #body="{ data: row }">
              {{ formatNumber(row.uniqueImpressionCount) }}
            </template>
          </Column>
          <Column field="swipeCount" header="Swipes" sortable style="width: 5rem" :pt="columnHeaderPt" body-class="tabular-nums text-xs">
            <template #body="{ data: row }">
              {{ formatNumber(row.swipeCount) }}
            </template>
          </Column>
          <Column field="applicationCount" header="Sollicitaties" sortable style="width: 5rem" :pt="columnHeaderPt" body-class="tabular-nums text-xs">
            <template #body="{ data: row }">
              {{ formatNumber(row.applicationCount) }}
            </template>
          </Column>
          <Column field="matchCount" header="Matches" sortable style="width: 5rem" :pt="columnHeaderPt" body-class="tabular-nums text-xs">
            <template #body="{ data: row }">
              {{ formatNumber(row.matchCount) }}
            </template>
          </Column>
          <Column field="hireCount" header="Hires" sortable style="width: 5rem" :pt="columnHeaderPt" body-class="tabular-nums text-xs">
            <template #body="{ data: row }">
              {{ formatNumber(row.hireCount) }}
            </template>
          </Column>
          <Column field="applicationRatio" header="Sollicitatie %" sortable style="width: 5rem" :pt="columnHeaderPt" body-class="tabular-nums text-xs">
            <template #body="{ data: row }">
              {{ formatRatio(row.applicationRatio) }}
            </template>
          </Column>
          <Column field="matchRatio" header="Match %" sortable style="width: 5rem" :pt="columnHeaderPt" body-class="tabular-nums text-xs">
            <template #body="{ data: row }">
              {{ formatRatio(row.matchRatio) }}
            </template>
          </Column>
        </DataTable>
      </CardWithHeader>

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PieChartStatistic
          title="Afwijzingsredenen"
          :items="rejectionItems"
          :total="stats.rejectionCount"
          :max-visible-items="5"
          empty-message="Geen afwijzingen voor dit bedrijf in de gekozen periode."
        >
          <template #subtitle>
            <span class="text-xs text-surface-400">{{ stats.rejectionCount }} totaal</span>
          </template>
        </PieChartStatistic>

        <PieChartStatistic
          title="Afsluitredenen matches"
          :items="closeItems"
          :total="stats.closedMatchCount + stats.hireCount"
          :max-visible-items="5"
          empty-message="Geen gesloten matches voor dit bedrijf in de gekozen periode."
        >
          <template #subtitle>
            <span class="text-xs text-surface-400">{{ stats.closedMatchCount + stats.hireCount }} totaal</span>
          </template>
        </PieChartStatistic>
      </div>

      <CardWithHeader
        v-if="metabaseIframeUrl"
        title="Metabase statistieken"
        :options="{ removeBodyPadding: true }"
      >
        <iframe :src="metabaseIframeUrl" width="100%" height="1000px" class="border-0" />
      </CardWithHeader>
    </template>
  </div>
</template>
